import { Page } from '@app/components/page/Page';
import { GroupIcon } from '@app/components/users/GroupIcon';
import { deleteGroup } from '@app/modules/admin/api';
import { DeleteOverflowActions } from '@app/modules/admin/components/DeleteOverflowActions';
import { EditButton } from '@app/modules/admin/components/EditButton';
import { useGroupQuery } from '@app/modules/admin/screens/groups/hooks/useGroupQuery';
import { capitalizeFirst } from '@mi-front/core';
import HelpIcon from '@mui/icons-material/Help';
import { Stack, Tooltip } from '@mui/material';
import { QueryView } from '@ui/utils/async-view/QueryView';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { GroupsBreadcrumbs } from '../header';
import { AdminGroupsViewTabContent } from './AdminGroupsViewTabContent';
import { buildAdminGroupsViewTabs } from './useAdminGroupsViewTabs';

/**
 * Admin group detail page with header tabs for info, station access, and permissions.
 */
export function ViewGroupPage() {
  const { id } = useParams<{ id: string }>();
  const { $t, formatMessage } = useIntl();
  const groupQuery = useGroupQuery(id);

  return (
    <QueryView query={groupQuery}>
      {(group) => {
        const canDelete = group.users.length === 0;
        const tabs = buildAdminGroupsViewTabs(group, $t);

        return (
          <Page
            data-cy="groups-view-screen"
            htmlTitle={`${capitalizeFirst(
              $t({
                id: 'common.entities.group',
                defaultMessage: 'grupo',
              }),
            )} ${group.name}`}
          >
            <Page.Header
              icon={<GroupIcon />}
              title={group.name}
              breadcrumbs={<GroupsBreadcrumbs />}
              tabs={tabs}
              actions={
                <Stack direction="row" spacing={1} alignItems="center">
                  <EditButton to={`/admin/groups/${group.id}/edit`} />
                  {!canDelete && (
                    <Tooltip
                      title={
                        <FormattedMessage
                          id="admin.groups.viewScreen.notDeletableHelp"
                          defaultMessage="Este grupo no se puede eliminar porque contiene usuarios asignados"
                        />
                      }
                    >
                      <HelpIcon fontSize="small" color="action" />
                    </Tooltip>
                  )}
                  <DeleteOverflowActions
                    onDelete={() => deleteGroup(group.id)}
                    title={formatMessage({
                      id: 'admin.groups.deleteScreen.title',
                      defaultMessage: 'Eliminar grupo',
                    })}
                    message={
                      <FormattedMessage
                        id="admin.groups.deleteScreen.prompt"
                        defaultMessage={`Deseas realmente eliminar el grupo <b>{name}</b>? {br} Esta acción no se puede deshacer.`}
                        values={{
                          name: group.name,
                          b: (chunks) => <strong>{chunks}</strong>,
                          br: <br />,
                        }}
                      />
                    }
                    successMessage={
                      <FormattedMessage
                        id="admin.groups.notifications.deleted"
                        defaultMessage="Grupo eliminado"
                      />
                    }
                    disabled={!canDelete}
                    redirectTo="/admin/groups"
                  />
                </Stack>
              }
            />
            <Page.Content>
              <AdminGroupsViewTabContent group={group} />
            </Page.Content>
          </Page>
        );
      }}
    </QueryView>
  );
}
